import { useEffect, useRef, useState, type RefObject } from 'react';
import { Dot } from './dot';
import { generatePathsAndDots, type PathDotSettings, type PathSettings } from './flow-chart-paths';
import * as styles from './styles.css';

type FlowChartProps = {
  containerRef: RefObject<HTMLDivElement | null>;
};

/** Animated SVG bezier overlay connecting the landing page's flow cards. */
export function FlowChart({ containerRef }: FlowChartProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [paths, setPaths] = useState<PathSettings[]>([]);
  const [pathDots, setPathDots] = useState<PathDotSettings[]>([]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const container = containerRef.current;
    if (overlay == null || container == null) {
      return;
    }

    const measure = () => {
      const rect = overlay.getBoundingClientRect();
      setSvgSize({ width: rect.width, height: rect.height });
      const generated = generatePathsAndDots(container);
      setPaths(generated.paths);
      setPathDots(generated.pathDots);
    };

    measure();
    const resizeObserver = new ResizeObserver(() => requestAnimationFrame(measure));
    resizeObserver.observe(overlay);
    return () => resizeObserver.disconnect();
  }, [containerRef]);

  return (
    <div ref={overlayRef} className={styles.flowChartOverlay}>
      <svg
        className={styles.flowChartSvg}
        width={svgSize.width}
        height={svgSize.height}
        viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
      >
        <g>
          {paths.map((path) => (
            <path
              key={path.id}
              id={path.id}
              d={path.d}
              fill="none"
              stroke={path.stroke.color}
              strokeDasharray={path.stroke.dasharray}
              strokeWidth={path.stroke.width}
            />
          ))}
        </g>
        <g>
          {pathDots.map((dot) => (
            <Dot key={dot.id} dot={dot} />
          ))}
        </g>
      </svg>
    </div>
  );
}
